import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getHomepageSectionML } from '../../services/api';
import { ArrowRight, Star, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Link } from 'react-router-dom';

const ProjectsSectionML = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => { loadProjectsContent(); }, [language]);

  const loadProjectsContent = async () => {
    setLoading(true);
    const normalizedLang = ['pt','en','fr','ar','zh'].includes(language) ? language : (language.startsWith('pt') ? 'pt' : 'en');
    const result = await getHomepageSectionML('projects', normalizedLang);
    if (result.success) setContent(result.data);
    setLoading(false);
  };

  const projects = content?.projects || [];
  const featuredProject = projects.find(p => p.featured) || projects[0];
  const otherProjects = projects.filter(p => p !== featuredProject);

  // Carousel auto-play
  useEffect(() => {
    if (otherProjects.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % otherProjects.length);
      }, 5000);
      return () => clearInterval(intervalRef.current);
    }
  }, [otherProjects.length]);

  const goToSlide = (idx) => {
    setCurrentSlide(idx);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % otherProjects.length);
    }, 5000);
  };

  const getVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?]+)/);
    return match ? match[1] : null;
  };

  if (loading || !content) {
    return <section className="bg-[#f8f9fa] py-24"><div className="container mx-auto px-6 text-center"><div className="animate-pulse">Loading...</div></div></section>;
  }

  const videoId = getVideoId(featuredProject?.video_url);

  return (
    <section className="bg-[#f8f9fa] py-24">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#00B4D8]" />
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00B4D8]">{content.label}</p>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#2B2D42] max-w-3xl tracking-tight leading-[1.1]">{content.heading}</h2>
        </div>

        {/* Featured Project Hero */}
        {featuredProject && (
          <div className="mb-20">
            <div className="rounded-3xl overflow-hidden shadow-2xl bg-[#023E8A]">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left: Image or Video */}
                <div className="relative lg:aspect-auto overflow-hidden bg-[#0a0a0a] min-h-[450px] flex items-center justify-center">
                  {showVideo && videoId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                      title="PEGADO Motors"
                      className="w-full h-full min-h-[450px] absolute inset-0"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        src={featuredProject.image}
                        alt={featuredProject.title}
                        className="w-full h-full object-contain max-h-[500px]"
                        style={{ animation: 'featuredZoom 8s ease-out forwards' }}
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800'; }}
                      />
                      <div className="absolute top-4 left-4 bg-[#FF6B35] text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-2 shadow-lg z-10">
                        <Star className="w-3 h-3 fill-white" /> TOP PROJECT
                      </div>
                      {videoId && (
                        <button
                          onClick={() => setShowVideo(true)}
                          className="absolute inset-0 flex items-center justify-center z-10 group/play"
                        >
                          <div className="w-20 h-20 bg-[#FF6B35] rounded-full flex items-center justify-center shadow-2xl group-hover/play:scale-110 transition-transform duration-300">
                            <Play className="w-8 h-8 text-white fill-white ml-1" />
                          </div>
                          <span className="absolute bottom-6 text-white text-sm font-semibold drop-shadow-lg">Watch Documentary</span>
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Right: Content */}
                <div className="text-white p-8 lg:p-12 flex flex-col justify-center">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#48CAE4] mb-3">{featuredProject.type}</span>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-4 leading-tight">{featuredProject.title}</h3>
                  <p className="text-sm text-white/60 mb-4">{featuredProject.location}</p>
                  <p className="text-base text-white/85 leading-relaxed mb-6">{featuredProject.description}</p>

                  {featuredProject.highlights && (
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {featuredProject.highlights.map((h, i) => (
                        <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                          <p className="text-2xl font-bold text-[#48CAE4]">{h.value}</p>
                          <p className="text-xs text-white/70">{h.label}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {videoId && !showVideo && (
                    <button onClick={() => setShowVideo(true)}
                      className="flex items-center gap-2 text-[#FF6B35] hover:text-[#48CAE4] transition-colors mb-4 text-sm font-semibold">
                      <Play className="w-4 h-4 fill-current" /> Watch PEGADO Motors Documentary
                    </button>
                  )}

                  {featuredProject.link && (
                    <Link to={featuredProject.link} className="text-sm font-semibold text-[#48CAE4] inline-flex items-center gap-2 hover:gap-3 transition-all">
                      View Full Case Study <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Other Projects — Carousel with Zoom-In */}
        {otherProjects.length > 0 && (
          <div className="relative">
            <div className="overflow-hidden rounded-2xl">
              <div
                className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * (100 / Math.min(otherProjects.length, 3))}%)` }}
              >
                {otherProjects.map((project, idx) => (
                  <div key={project.id} className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4">
                    <Link to={project.link || '#'} className="block h-full">
                      <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 group cursor-pointer h-full">
                        <div className="relative overflow-hidden aspect-video">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover"
                            style={{ animation: idx === currentSlide ? 'carouselZoom 6s ease-out forwards' : 'none' }}
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600'; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </div>
                        <div className="p-6">
                          <span className="text-xs font-bold uppercase tracking-wider text-[#00B4D8] bg-[#00B4D8]/10 px-2 py-1 rounded">{project.type}</span>
                          <h4 className="text-lg font-bold text-[#2B2D42] mt-3 mb-2 group-hover:text-[#FF6B35] transition-colors line-clamp-2">{project.title}</h4>
                          <p className="text-sm text-[#ADB5BD] mb-2">{project.location}</p>
                          <p className="text-sm text-[#6C757D] leading-relaxed line-clamp-3">{project.description}</p>
                          <span className="text-sm font-semibold text-[#00B4D8] mt-4 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                            View Case Study <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel Nav */}
            {otherProjects.length > 3 && (
              <>
                <button onClick={() => goToSlide((currentSlide - 1 + otherProjects.length) % otherProjects.length)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-[#FF6B35] hover:text-white transition-all z-10">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => goToSlide((currentSlide + 1) % otherProjects.length)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-[#FF6B35] hover:text-white transition-all z-10">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {otherProjects.map((_, idx) => (
                <button key={idx} onClick={() => goToSlide(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${idx === currentSlide ? 'bg-[#FF6B35] w-8' : 'bg-gray-300 w-2 hover:bg-gray-400'}`} />
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes featuredZoom { 0% { transform: scale(1); } 100% { transform: scale(1.06); } }
        @keyframes carouselZoom { 0% { transform: scale(1); } 100% { transform: scale(1.08); } }
      `}</style>
    </section>
  );
};

export default ProjectsSectionML;
